import { App, ConfigProvider, theme } from 'antd';
import type { ThemeConfig } from 'antd';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';

import { accentColors, dsTokens, dsType } from '@/shared/theme/tokens';
import { useTheme } from './ThemeProvider';

/**
 * Configuration du thème Ant Design pour Autocop
 * Basé sur le design system Figma et les tokens Ant Design v5
 */
const antdTheme: ThemeConfig = {
  token: {
    // Couleurs principales (Brand) — pulled from tokens.ts
    colorPrimary: accentColors.primary,
    colorPrimaryBg: '#F0F5FF',
    colorPrimaryBgHover: '#BAE0FF',
    colorPrimaryBorder: '#91CAFF',
    colorPrimaryBorderHover: '#69B1FF',
    colorPrimaryHover: accentColors.primaryHover,
    colorPrimaryActive: accentColors.primaryActive,
    colorPrimaryTextHover: accentColors.primaryHover,
    colorPrimaryText: accentColors.primary,
    colorPrimaryTextActive: accentColors.primaryActive,

    // Couleurs de succès (seules les couleurs principales, le reste est géré par l'algorithme)
    colorSuccess: '#52C41A',

    // Couleurs d'avertissement
    colorWarning: '#FAAD14',

    // Couleurs d'erreur
    colorError: '#FF4D4F',

    // Couleurs d'information
    colorInfo: '#1677FF',

    // Couleurs de liens
    colorLink: accentColors.primary,
    colorLinkHover: '#69B1FF',
    colorLinkActive: accentColors.primaryActive,

    // Note: Les couleurs de texte, fond et bordure sont gérées automatiquement
    // par darkAlgorithm / defaultAlgorithm pour supporter le dark mode

    // Typographie — Inter (aligned with new DS tokens + index.html)
    fontFamily: dsType.families.sans,
    fontSize: 13,
    fontSizeSM: 11,
    fontSizeLG: 15,
    fontSizeXL: 18,
    fontSizeHeading1: 24,
    fontSizeHeading2: 20,
    fontSizeHeading3: 18,
    fontSizeHeading4: 15,
    fontSizeHeading5: 13,
    fontWeightStrong: 600,

    // Line Heights
    lineHeight: 1.45,
    lineHeightSM: 1.5,
    lineHeightLG: 1.5,
    lineHeightHeading1: 1.2,
    lineHeightHeading2: 1.27,
    lineHeightHeading3: 1.33,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,

    // Tailles de contrôles
    controlHeight: 32,
    controlHeightSM: 24,
    controlHeightLG: 38,

    // Border Radius (SaaS sharper)
    borderRadius: 8,
    borderRadiusSM: 6,
    borderRadiusLG: 10,
    borderRadiusXS: 4,

    // Spacing
    margin: 16,
    marginXS: 8,
    marginSM: 12,
    marginLG: 24,
    marginXL: 32,
    marginXXL: 48,
    padding: 16,
    paddingXS: 8,
    paddingSM: 12,
    paddingLG: 24,
    paddingXL: 32,

    // Ombres
    boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
  },
  components: {
    // Boutons
    Button: {
      primaryShadow: '0px 2px 0px 0px rgba(5, 145, 255, 0.1)',
      defaultShadow: '0px 2px 0px 0px rgba(0, 0, 0, 0.02)',
      borderRadius: 8,
      controlHeight: 32,
      paddingContentHorizontal: 15,
    },

    // Tables (d'après le Figma)
    Table: {
      headerBorderRadius: 0,
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
      fontWeightStrong: 600,
    },

    // Checkbox (carrés arrondis)
    Checkbox: {
      borderRadiusSM: 6,
    },

    // Inputs
    Input: {
      borderRadius: 8,
      paddingBlock: 4,
      paddingInline: 11,
    },

    // InputNumber
    InputNumber: {
      borderRadius: 8,
      handleVisible: true,
    },

    // Tags
    Tag: {
      borderRadiusSM: 4,
    },

    // Select
    Select: {
      borderRadius: 8,
    },

    // Modal
    Modal: {
      borderRadiusLG: 12,
    },

    // Card
    Card: {
      borderRadiusLG: 10,
    },

    // Breadcrumb
    Breadcrumb: {
      fontSize: 13,
    },

    // Tooltip
    Tooltip: {
      borderRadius: 10,
    },

    // Switch
    Switch: {
      colorPrimary: accentColors.primary,
      colorPrimaryHover: accentColors.primaryHover,
    },

  },
};

interface AntdThemeProviderProps {
  children: ReactNode;
}

export const AntdThemeProvider: FC<AntdThemeProviderProps> = ({ children }) => {
  const { theme: currentTheme } = useTheme();
  const { i18n } = useTranslation();
  const antdLocale = i18n.language?.startsWith('en') ? enUS : frFR;

  const themeConfig = useMemo<ThemeConfig>(() => {
    const ds = dsTokens[currentTheme];
    return {
      ...antdTheme,
      algorithm: currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      components: {
        ...antdTheme.components,
        Table: {
          ...antdTheme.components?.Table,
          headerBg: ds['surface.subtle'],
        },
        Tooltip: {
          ...antdTheme.components?.Tooltip,
          colorBgSpotlight: ds['surface.elevated'],
          colorTextLightSolid: ds['text.primary'],
        },
      },
      token: {
        ...antdTheme.token,
        // Surfaces — aligned with DS tokens so AntD components living
        // inside the new shell card match the surrounding background.
        colorBgLayout:    ds['surface.default'],
        colorBgContainer: ds['surface.default'],
        colorBgElevated:  ds['surface.elevated'],
        colorBgSpotlight: ds['surface.elevated'],

        // Borders
        colorBorder:          ds['border.default'],
        colorBorderSecondary: ds['border.subtle'],

        // Text
        colorText:            ds['text.primary'],
        colorTextSecondary:   ds['text.secondary'],
        colorTextTertiary:    ds['text.tertiary'],

        // Brand — primary indigo (light = #3B82F6, dark = #818CF8)
        colorPrimary:           ds['brand.primary'],
        colorPrimaryHover:      ds['brand.primaryHover'],
        colorPrimaryActive:     ds['brand.primaryHover'],
        colorPrimaryBg:         ds['brand.primarySoft'],
        colorPrimaryBgHover:    ds['brand.primarySoft'],
        colorPrimaryText:       ds['brand.primary'],
        colorPrimaryTextHover:  ds['brand.primaryHover'],
        colorPrimaryTextActive: ds['brand.primaryHover'],
        colorLink:              ds['brand.primary'],
        colorLinkHover:         ds['brand.primaryHover'],
        colorLinkActive:        ds['brand.primaryHover'],
        colorInfo:              ds['info.default'],
        colorSuccess:           ds['success.default'],
        colorError:             ds['error.default'],
        colorWarning:           ds['warning.default'],
      },
    };
  }, [currentTheme]);

  return (
    <ConfigProvider theme={themeConfig} locale={antdLocale}>
      <App>
        {children}
      </App>
    </ConfigProvider>
  );
};
